import { useMemo, useState } from 'react';
import clsx from 'clsx';
import { MegaphoneIcon } from '@heroicons/react/24/outline';
import { SiPortalEmBreve } from '../../components/SiPortalEmBreve';
import { PUBLICACOES_POR_PAGINA, estaFixada, ordenarFeed } from '../publicacao.definitions';
import type { MeuEstadoPublicacao, Publicacao } from '../publicacao.types';
import { CATALOGO_FILTRO_FEED, filtroDoFeed, filtroEfetivo, opcoesComConteudo } from '../filtrosCategoria';
import { PublicacaoCard } from './PublicacaoCard';

interface FeedPublicacoesProps {
  publicacoes: Publicacao[];
  estados: Map<string, MeuEstadoPublicacao>;
  colaboradorId: string | null | undefined;
  agora: number;
  isLoading: boolean;
  linkDe: (publicacao: Publicacao) => string;
}

/** Informativos da Início: fixadas primeiro, depois as mais novas; 6 por vez, com filtro por categoria. */
export function FeedPublicacoes({ publicacoes, estados, colaboradorId, agora, isLoading, linkDe }: FeedPublicacoesProps) {
  const [escolhida, setEscolhida] = useState('');
  // Só as categorias com publicação (onboarding tem filtro próprio); "Todas" só com mais de uma.
  const opcoes = useMemo(
    () => opcoesComConteudo(CATALOGO_FILTRO_FEED, new Set(publicacoes.map(filtroDoFeed)), 'Todas'),
    [publicacoes]
  );
  const categoria = filtroEfetivo(escolhida, opcoes);
  const [quantidade, setQuantidade] = useState(PUBLICACOES_POR_PAGINA);

  const filtradas = useMemo(
    () =>
      ordenarFeed(publicacoes, agora).filter((p) => !categoria || filtroDoFeed(p) === categoria),
    [publicacoes, agora, categoria]
  );
  const exibidas = filtradas.slice(0, quantidade);

  const trocarCategoria = (valor: string) => {
    setEscolhida(valor);
    setQuantidade(PUBLICACOES_POR_PAGINA);
  };

  return (
    <section className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="font-montserrat text-base font-semibold text-raro-gray-900">Informativos</h2>
        {publicacoes.length > 0 && (
          <div className="flex flex-wrap gap-1 text-xs font-medium" role="group" aria-label="Filtrar por categoria">
            {opcoes.map((opcao) => (
              <button
                key={opcao.valor || 'todas'}
                type="button"
                onClick={() => trocarCategoria(opcao.valor)}
                aria-pressed={categoria === opcao.valor}
                className={clsx(
                  'rounded-full px-3 py-1.5 transition-colors',
                  categoria === opcao.valor
                    ? 'bg-primary-50 font-semibold text-primary-700 ring-1 ring-primary-200'
                    : 'text-raro-gray-600 hover:bg-white hover:text-primary-700'
                )}
              >
                {opcao.label}
              </button>
            ))}
          </div>
        )}
      </div>

      {isLoading ? (
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="h-64 animate-pulse rounded-2xl bg-raro-gray-100" />
          <div className="h-64 animate-pulse rounded-2xl bg-raro-gray-100" />
        </div>
      ) : publicacoes.length === 0 ? (
        <SiPortalEmBreve
          icon={MegaphoneIcon}
          titulo="Nenhuma publicação ainda"
          descricao="Alertas, dicas e comunicados do time de Segurança da Informação vão aparecer aqui."
        />
      ) : filtradas.length === 0 ? (
        <p className="rounded-2xl border border-dashed border-raro-gray-300 bg-white px-4 py-6 text-center text-sm text-raro-gray-500">
          Nenhuma publicação nesta categoria.
        </p>
      ) : (
        <>
          <div className="grid gap-4 sm:grid-cols-2">
            {exibidas.map((publicacao) => (
              <PublicacaoCard
                key={publicacao.id}
                publicacao={publicacao}
                estado={estados.get(publicacao.id)}
                fixada={estaFixada(publicacao, agora)}
                colaboradorId={colaboradorId}
                agora={agora}
                to={linkDe(publicacao)}
              />
            ))}
          </div>
          {filtradas.length > PUBLICACOES_POR_PAGINA && (
            <div className="flex flex-col items-center gap-1 pt-2">
              {exibidas.length < filtradas.length && (
                <button
                  type="button"
                  onClick={() => setQuantidade((atual) => atual + PUBLICACOES_POR_PAGINA)}
                  className="rounded-full border border-primary-200 bg-white px-5 py-2 text-sm font-semibold text-primary-700 hover:bg-primary-50"
                >
                  Mostrar mais publicações
                </button>
              )}
              <p className="text-xs text-raro-gray-400">
                Mostrando {exibidas.length} de {filtradas.length}
              </p>
            </div>
          )}
        </>
      )}
    </section>
  );
}
